import { postRecyclingPackageProducts } from '@/src/apis/rest/recyclingProducts';
import { PostRecyclingPackageProductRequestBody } from '@/src/apis/rest/recyclingProducts.dtos';
import Button from '@/src/components/atom/Button';
import Dialog from '@/src/components/atom/Dialog';
import Input from '@/src/components/atom/Input';
import RadioGroup from '@/src/components/atom/RadioGroup';
import FormContent from '@/src/components/molecule/FormContent';
import { Form, FormField } from '@/src/components/ui/form';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { OPTIONS_CONST } from '@/src/constants/options';
import { TOAST_CONST } from '@/src/constants/toast';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { usePrompt } from '@/src/hooks/usePrompt';
import { toast } from '@/src/hooks/useToast';
import { APIError } from '@/src/models/common/apiError';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

interface RecyclingPackageProductCreateModalprops {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  onOk: () => void;
  unitList: { key: string; value: string }[];
}

const formSchema = () => {
  return z.object({
    name: z.string().min(1),
    weight: z.number(),
    maximumWeight: z.number(),
    carbonEmissionReduction: z.number(),
    pineScore: z.number(),
    unit: z.string(),
    isForApp: z.boolean().optional(),
  });
};

const DEFAULT_VALUES: Partial<PostRecyclingPackageProductRequestBody> = {
  unit: 'BOX',
  isForApp: true,
};

const RecyclingPackageProductCreateModal = ({
  open: openProps,
  onOpenChange: onOpenChangeProps,
  onOk,
  unitList,
}: RecyclingPackageProductCreateModalprops) => {
  const {
    Dialog: BlockAlertDialog,
    isOpen: isBlockAlertOpen,
    openDialog: openBlockAlertDialog,
    closeDialog: closeBlockAlertDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });

  const {
    Dialog: SaveDialog,
    isOpen: isSaveOpen,
    openDialog: openSaveDialog,
    closeDialog: closeSaveDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });

  const {
    Dialog: DuplicateErrorDialog,
    isOpen: isDuplicateErrorOpen,
    openDialog: openDuplicateErrorDialog,
    closeDialog: closeDuplicateErrorDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });

  const formRef = useRef<HTMLFormElement>(null);

  const form = useForm<PostRecyclingPackageProductRequestBody>({
    resolver: zodResolver(formSchema()),
    defaultValues: DEFAULT_VALUES,
    reValidateMode: 'onSubmit',
  });

  const handleCloseModal = () => {
    if (form.formState.isDirty) {
      openBlockAlertDialog();
    } else {
      onOpenChangeProps(false);
    }
  };

  usePrompt(form.formState.isDirty && !form.formState.isSubmitted);

  const postRecycingPackageProductMutation = useMutation({
    mutationFn: postRecyclingPackageProducts,
  });

  const handleSubmit = (
    values: Partial<PostRecyclingPackageProductRequestBody>,
  ) => {
    postRecycingPackageProductMutation.mutate(values, {
      onSuccess: () => {
        toast({
          variant: 'success',
          title: TOAST_CONST.saveSuccess.title,
          description: TOAST_CONST.saveSuccess.description,
        });

        onOpenChangeProps(false);
        onOk();
      },
      onError: e => {
        const { error } = e as unknown as APIError;

        if (error === 'DUPLICATE_RECYCLING_PACKAGE_FOR_APP') {
          openDuplicateErrorDialog();
        } else {
          toast({
            variant: 'error',
            title: TOAST_CONST.error.title,
            description: e.message,
          });
        }
      },
    });
  };

  const handleBlockModalOk = () => {
    onOpenChangeProps(false);
  };
  const renderFooter = () => {
    return (
      <div className="flex justify-end gap-2">
        <Button
          className="flex-1"
          buttonColor="outline"
          onClick={handleCloseModal}
        >
          {BUTTON_CONST.cancel}
        </Button>
        <Button
          type="button"
          className="flex-1"
          disabled={!form.formState.isValid}
          onClick={openSaveDialog}
          isLoading={postRecycingPackageProductMutation.isPending}
        >
          {BUTTON_CONST.save}
        </Button>
      </div>
    );
  };

  return (
    <>
      <Dialog
        open={openProps}
        onOpenChange={handleCloseModal}
        contentProps={{
          style: { width: 'calc(100vw - 24px)' },
          className: 'max-w-[697px]',
        }}
        title="수거 기본 품목 등록"
        footer={renderFooter()}
      >
        <Form {...form}>
          <form
            ref={formRef}
            className="flex flex-col gap-6"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="수거 품목명" isRequired error={error}>
                    <Input
                      onClear={() => field.onChange('')}
                      placeholder="수거 품목 명을 입력해 주세요"
                      {...field}
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="weight"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="무게 (g)" isRequired error={error}>
                    <Input
                      type="number"
                      min={0}
                      {...field}
                      onClear={() => field.onChange(undefined)}
                      placeholder="무게를 입력해 주세요"
                      onChange={e =>
                        field.onChange(
                          e.target.value ? Number(e.target.value) : undefined,
                        )
                      }
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="carbonEmissionReduction"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="탄소 감축량 (g)" isRequired error={error}>
                    <Input
                      type="number"
                      min={0}
                      {...field}
                      onClear={() => field.onChange(undefined)}
                      placeholder="탄소 감축량을 입력해 주세요"
                      onChange={e =>
                        field.onChange(
                          e.target.value ? Number(e.target.value) : undefined,
                        )
                      }
                      isDecimal
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="pineScore"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="소나무 지수" isRequired error={error}>
                    <Input
                      type="number"
                      min={0}
                      {...field}
                      onClear={() => field.onChange(undefined)}
                      placeholder="소나무 지수를 입력해 주세요"
                      onChange={e =>
                        field.onChange(
                          e.target.value ? Number(e.target.value) : undefined,
                        )
                      }
                      isDecimal
                    />
                  </FormContent>
                );
              }}
            />
            {unitList && (
              <FormField
                control={form.control}
                name="unit"
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormContent label="단위" error={error}>
                      <RadioGroup
                        optionList={unitList.map((item: any) => ({
                          label: item.value,
                          value: item.key,
                        }))}
                        rootProps={{
                          value: field.value,
                          onValueChange: (e: string) => {
                            field.onChange(e);
                          },
                        }}
                      />
                    </FormContent>
                  );
                }}
              />
            )}
            <FormField
              control={form.control}
              name="maximumWeight"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="최대 무게 (g)" isRequired error={error}>
                    <Input
                      type="number"
                      min={0}
                      {...field}
                      onClear={() => field.onChange(undefined)}
                      placeholder="1개의 수거 기본 품목 당 최대 수용 무게를 입력해 주세요"
                      onChange={e =>
                        field.onChange(
                          e.target.value ? Number(e.target.value) : undefined,
                        )
                      }
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="isForApp"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="앱 내 적용 여부" error={error}>
                    <RadioGroup
                      optionList={[
                        { label: OPTIONS_CONST.applied, value: 'true' },
                        { label: OPTIONS_CONST.notApplied, value: 'false' },
                      ]}
                      rootProps={{
                        value: field.value ? 'true' : 'false',
                        onValueChange: (e: string) =>
                          field.onChange(e === 'true'),
                      }}
                    />
                  </FormContent>
                );
              }}
            />
          </form>
        </Form>
        <BlockAlertDialog
          open={isBlockAlertOpen}
          onOpenChange={closeBlockAlertDialog}
          onCancel={() => {
            closeBlockAlertDialog();
          }}
          onOk={handleBlockModalOk}
          title="취소"
        >
          변경사항은 저장되지 않습니다. 취소하시겠습니까?
        </BlockAlertDialog>
        <DuplicateErrorDialog
          open={isDuplicateErrorOpen}
          onOpenChange={closeDuplicateErrorDialog}
          onCancel={() => {
            closeDuplicateErrorDialog();
          }}
          onOk={closeDuplicateErrorDialog}
          title="수거 기본 품목 갯수 제한"
        >
          앱 내 수거 품목은 1개만 지정할 수 있어요
        </DuplicateErrorDialog>
      </Dialog>

      {isSaveOpen && (
        <SaveDialog
          open={isSaveOpen}
          onOk={() => {
            handleSubmit(form.watch());
          }}
          onOpenChange={e => {
            if (e) {
              openSaveDialog();
            } else {
              closeSaveDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.save}
          isLoading={postRecycingPackageProductMutation.isPending}
        />
      )}
    </>
  );
};

export default RecyclingPackageProductCreateModal;
